import { useCallback, useRef, useState } from 'react';

import { useAudioAnalyser } from '@/entities/audio-recording';
import { audioLevelMeterConfig, SILENCE_THRESHOLD } from '@/entities/session';

interface AudioLevelMeterProps {
    mode?: keyof typeof audioLevelMeterConfig;
    audioStream?: MediaStream;
    label?: string;
    onAudioLevelChange?: (isAudioLevelLow: boolean) => void;
}

const getLevelDescription = (average: number, maxLevel: number) => {
    const percentage = (average / maxLevel) * 100;
    if (percentage === 0) return 'Muted';
    if (percentage < 30) return 'Low';
    if (percentage < 70) return 'Medium';
    return 'High';
};

export function AudioLevelMeter({
    mode = 'default',
    audioStream,
    label = 'Microphone',
    onAudioLevelChange,
}: AudioLevelMeterProps) {
    const [currentAverage, setCurrentAverage] = useState(0);
    const smoothedAverageRef = useRef(0);
    const lastUiUpdateAtRef = useRef(0);

    const {
        canvas: canvasConfig,
        bars,
        colors,
        maxLevel,
        smoothingFactor,
        updateFps,
    } = audioLevelMeterConfig[mode];
    const barWidth = (canvasConfig.width - bars.spacing * (bars.count - 1)) / bars.count;
    const updateIntervalMs = 1000 / updateFps;

    const audioLevelDescription = getLevelDescription(currentAverage, maxLevel);

    let numBarsToFill = 0;
    if (currentAverage >= SILENCE_THRESHOLD) {
        const normalizedLevel =
            (currentAverage - SILENCE_THRESHOLD) / (maxLevel - SILENCE_THRESHOLD);
        const clampedLevel = Math.max(0, Math.min(1, normalizedLevel));
        const sensitivity = Math.sqrt(clampedLevel);
        numBarsToFill = Math.ceil(sensitivity * bars.count);
    }

    const onAnalyze = useCallback(
        (average: number) => {
            smoothedAverageRef.current =
                smoothedAverageRef.current * (1 - smoothingFactor) + average * smoothingFactor;

            const smoothedAverage = smoothedAverageRef.current;

            const now = performance.now();
            if (now - lastUiUpdateAtRef.current < updateIntervalMs) {
                return;
            }

            lastUiUpdateAtRef.current = now;
            setCurrentAverage(Math.round(smoothedAverage));
            onAudioLevelChange?.(smoothedAverage < SILENCE_THRESHOLD);
        },
        [smoothingFactor, onAudioLevelChange, updateIntervalMs],
    );

    useAudioAnalyser({ audioStream, onAnalyze });

    return (
        <svg
            width={canvasConfig.width}
            height={canvasConfig.height}
            className="mx-auto flex-shrink-0"
            role="progressbar"
            aria-valuetext={audioLevelDescription}
            aria-label={`${label} audio level: ${audioLevelDescription}`}
            aria-valuemin={0}
            aria-valuemax={maxLevel}
            aria-valuenow={Math.round(currentAverage)}
        >
            {Array.from({ length: bars.count }, (_, i) => i).map((i) => (
                <rect
                    key={i}
                    x={i * (barWidth + bars.spacing)}
                    y={0}
                    width={barWidth}
                    height={canvasConfig.height}
                    rx={bars.borderRadius}
                    ry={bars.borderRadius}
                    className={i < numBarsToFill ? colors.active : colors.inactive}
                />
            ))}
        </svg>
    );
}
